import {
  type CamInsightsRecordingFetcherInput,
  fetchCamInsightsRecording,
} from 'common/api/cameras';
import { SL_WEB_CAM_PAYWALL_REDESIGN, SL_WEB_FREEMIUM_CAM_HIGHLIGHTS } from 'common/treatments';
import { RewindVideo } from 'components/RewindCamPlayer/plugins/rewindStitcher/src/rewindStitcher.types';
import config from 'config';
import { roundToNearestMinutes } from 'date-fns';
import useCurrentTimestamp, { CurrentTimestamp } from 'hooks/useCurrentTimestamp';
import useNonPremiumRewindTime from 'hooks/useNonPremiumRewindTime';
import { useRouter } from 'next/router';
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUserPermissionStatus } from 'selectors/user';
import useSWR from 'swr';
import { Camera } from 'types/camera';
import { RewindRecording } from 'types/recordings';
import { generateSmartRewindPlaylistBlocks } from 'utils/rewinds/generateSmartRewindPlaylistBlocks';
import {
  firstPreviouslyCompletePlaylist,
  calculateMatchingPlaylistBlock,
} from 'utils/rewinds/playlistBlocks';
import { RewindPlaylists, SmartRewindPlaylistBlock } from 'utils/rewinds/playlistBlocks.types';
import { ceilToMidnightForTZ, floorToMidnightForTZ, oneDay, oneHour, oneMinute } from 'utils/time';
import { useTreatments } from 'utils/treatments';

const DEFAULT_DAYS_TO_LOAD = config.numberOfRewindDays;
const PLAYLIST_BLOCK_LENGTH = 30; // 30 minutes
const REWIND_PLAYLIST_BLOCK_OVERLAP = 0;
const FREEMIUM_HIGHLIGHTS_INITIAL_RANGE_IN_MS = oneDay + oneHour;

export const REWIND_PLAYLIST_BLOCK_LENGTH_IN_MS = PLAYLIST_BLOCK_LENGTH * oneMinute;
export const REWIND_PLAYLIST_BLOCK_OVERLAP_IN_MS = REWIND_PLAYLIST_BLOCK_OVERLAP * oneMinute;

const queryStringToTimestamp = (queryTimestamp: string | string[] | undefined) => {
  const timestamp = Array.isArray(queryTimestamp) ? queryTimestamp[0] : queryTimestamp;
  return timestamp ? Number(timestamp) : undefined;
};

const getRewindsRequestKey = ({
  camId,
  startDate,
  endDate,
  surfHeightUnit,
}: CamInsightsRecordingFetcherInput) => [camId, startDate, endDate, surfHeightUnit].join(':');

export const RewindsContext = createContext({} as RewindsContextProps);

interface RewindsContextProviderProps {
  children: React.ReactNode;
  surfHeightUnit: string | undefined;
  selectedCamera: Camera;
  timezone: string;
}

interface RewindsResponseData {
  cameraId: string;
  requestKey: string;
  recordings: RewindRecording[];
}

interface RewindsFetcherResponse {
  cameraId: string;
  requestKey: string;
  recordings: RewindRecording[] | null;
}

export interface RewindsContextProps {
  rewindsPlaylists: RewindPlaylists | undefined;
  currentPlaylist: SmartRewindPlaylistBlock | undefined;
  updateCurrentPlaylist: (newPlaylistTimestamp: string) => void;
  error: boolean;
  reFetch: () => void;
  rewindsLoading: boolean;
  expandedRewindsLoading: boolean;
  expandedRewindsError: boolean;
  hasLoadedExpandedRewinds: boolean;
  loadExpandedRewinds: () => Promise<void>;
  isCurrentPlaylistLatest: boolean;
  previouslyCompletedPlaylists: {
    morning?: SmartRewindPlaylistBlock;
    night?: SmartRewindPlaylistBlock;
  };
  currentTimestamp: CurrentTimestamp;
  isNonPremiumPlaylist: boolean;
  rewindList: RewindVideo[];
  nextAvailableRewindFromLive: string | undefined;
  bucketStartTimeInMs: number | null;
  bucketEndTimeInMs: number | null;
}

export const RewindsContextProvider = ({
  children,
  surfHeightUnit,
  selectedCamera,
  timezone,
}: RewindsContextProviderProps) => {
  const [rewindsPlaylists, setRewindsPlaylists] = useState<RewindPlaylists | undefined>(undefined);
  const [currentPlaylist, setCurrentPlaylist] = useState<SmartRewindPlaylistBlock | undefined>(
    undefined,
  );
  const now = useMemo(() => new Date().getTime(), []);
  const router = useRouter();
  const { query } = router;
  const routerIsReady = router.isReady !== false;
  const currentTimestamp = useCurrentTimestamp();
  const { currentTimestampRef, setCurrentTimestamp } = currentTimestamp;
  const treatments = useTreatments();
  const { hasAdFreeCamPermissions, hasAdSupportedCamPermissions, hasCamRewindPermissions } =
    useUserPermissionStatus();
  const initialisedWithTimestamp = useRef(false);
  const capturedInitialRouterQuery = useRef(false);
  if (routerIsReady && !capturedInitialRouterQuery.current) {
    capturedInitialRouterQuery.current = true;
    initialisedWithTimestamp.current = !!queryStringToTimestamp(query.currentTimestamp);
  }
  // Rounded to next 5 mins so we hit Edge Cache
  const endDate = useRef<number>(now);
  const roundedEnd = roundToNearestMinutes(endDate.current, { nearestTo: 5 }).getTime();

  const fullStartDate = endDate.current - DEFAULT_DAYS_TO_LOAD * oneDay;
  const roundedFullStart = ceilToMidnightForTZ(fullStartDate, timezone);
  const initialShortStart = roundedEnd - REWIND_PLAYLIST_BLOCK_LENGTH_IN_MS;
  const freemiumHighlightsTreatment = treatments?.[SL_WEB_FREEMIUM_CAM_HIGHLIGHTS];
  const shouldUseFreemiumHighlightsInitialRange =
    !hasCamRewindPermissions &&
    (freemiumHighlightsTreatment === 'on' || freemiumHighlightsTreatment === 'preroll');
  const freemiumHighlightsInitialStart = Math.max(
    roundedFullStart,
    roundedEnd - FREEMIUM_HIGHLIGHTS_INITIAL_RANGE_IN_MS,
  );
  const queryPageSection = routerIsReady ? query.pageSection : undefined;
  const queryTimestampFromUrl = routerIsReady
    ? queryStringToTimestamp(query.currentTimestamp)
    : undefined;

  const fullRewindsRange = useMemo(
    () => ({
      startDate: roundedFullStart,
      endDate: roundedEnd,
    }),
    [roundedFullStart, roundedEnd],
  );

  // Capture share/deep-link intent once when the router is ready so later playhead updates
  // do not reshape the initial SWR key mid-session.
  const shareRewindIntentRef = useRef<{
    pageSection?: string | string[];
    timestamp?: number;
  } | null>(null);
  if (routerIsReady && shareRewindIntentRef.current === null) {
    shareRewindIntentRef.current = {
      pageSection: queryPageSection,
      timestamp: queryTimestampFromUrl,
    };
  }
  const shareRewindIntent = shareRewindIntentRef.current;

  const initialRewindsRange = useMemo(() => {
    const shareTimestamp = shareRewindIntent?.timestamp;
    const sharePageSection = shareRewindIntent?.pageSection;
    const isShareTimestampInsideRewindWindow =
      !!shareTimestamp && shareTimestamp >= roundedFullStart && shareTimestamp <= roundedEnd;
    const isShareOutsideShortWindow =
      isShareTimestampInsideRewindWindow && shareTimestamp < initialShortStart;

    // Rewind share deep links (`pageSection=rewind`) outside the short live window must win
    // over freemium short-circuiting. Fetch the full configured history so the requested
    // playlist is guaranteed to exist — narrow 2-block windows previously missed blocks
    // after cache rounding.
    if (sharePageSection === 'rewind' && isShareOutsideShortWindow) {
      return {
        startDate: roundedFullStart,
        endDate: roundedEnd,
      };
    }

    if (shouldUseFreemiumHighlightsInitialRange) {
      return {
        startDate: freemiumHighlightsInitialStart,
        endDate: roundedEnd,
      };
    }

    return {
      startDate: initialShortStart,
      endDate: roundedEnd,
    };
  }, [
    freemiumHighlightsInitialStart,
    initialShortStart,
    roundedEnd,
    roundedFullStart,
    shareRewindIntent,
    shouldUseFreemiumHighlightsInitialRange,
  ]);

  const initialFetchIsFullHistory =
    initialRewindsRange.startDate === fullRewindsRange.startDate &&
    initialRewindsRange.endDate === fullRewindsRange.endDate;

  const [responseData, setData] = useState<RewindsResponseData | undefined>(undefined);
  const [rewindsPlaylistsCameraId, setRewindsPlaylistsCameraId] = useState<string | undefined>();

  const paywallRedesignActive =
    !hasAdFreeCamPermissions &&
    !hasAdSupportedCamPermissions &&
    treatments?.[SL_WEB_CAM_PAYWALL_REDESIGN] === 'on';

  const nonPremiumRewindTime = useNonPremiumRewindTime(now, hasAdFreeCamPermissions);
  const [nonPremiumPlaylistKey, setNonPremiumPlaylistKey] = useState<string | undefined>();
  const [currentPlaylistKey, setCurrentPlaylistKey] = useState<string | undefined>();
  const [currentPlaylistCameraId, setCurrentPlaylistCameraId] = useState<string | undefined>();
  const [userSelectedPlaylistKey, setUserSelectedPlaylistKey] = useState<string | undefined>();
  const [expandedRewindsLoading, setExpandedRewindsLoading] = useState(false);
  const [expandedRewindsError, setExpandedRewindsError] = useState(false);
  const [hasLoadedExpandedRewinds, setHasLoadedExpandedRewinds] =
    useState(initialFetchIsFullHistory);
  const expandedRewindsPromise = useRef<Promise<void> | null>(null);
  const expandedRewindsRequestId = useRef(0);
  const skipNextPlaylistSelection = useRef(false);
  const hasInitializedRewindsRequest = useRef(false);
  const expandedRewindsRequestKey = useMemo(
    () =>
      [
        selectedCamera._id,
        fullRewindsRange.startDate,
        fullRewindsRange.endDate,
        surfHeightUnit,
      ].join(':'),
    [fullRewindsRange.endDate, fullRewindsRange.startDate, selectedCamera._id, surfHeightUnit],
  );
  const expandedRewindsRequestKeyRef = useRef(expandedRewindsRequestKey);
  expandedRewindsRequestKeyRef.current = expandedRewindsRequestKey;

  const rewindsKey = useMemo<CamInsightsRecordingFetcherInput | null>(
    () =>
      routerIsReady
        ? {
            camId: selectedCamera._id,
            startDate: initialRewindsRange.startDate,
            endDate: initialRewindsRange.endDate,
            surfHeightUnit,
          }
        : null,
    [
      initialRewindsRange.endDate,
      initialRewindsRange.startDate,
      routerIsReady,
      selectedCamera._id,
      surfHeightUnit,
    ],
  );

  const fetchRewindsRecordingData = useCallback(
    async (input: CamInsightsRecordingFetcherInput): Promise<RewindsFetcherResponse> => ({
      cameraId: input.camId,
      requestKey: getRewindsRequestKey(input),
      recordings: await fetchCamInsightsRecording(input),
    }),
    [],
  );

  const { data, error, mutate, isLoading } = useSWR(rewindsKey, fetchRewindsRecordingData, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });

  const [rewindsLoading, setRewindsLoading] = useState<boolean>(!routerIsReady || isLoading);

  const normalizeRecordingData = useCallback(
    (recordings: RewindRecording[] | null | undefined) =>
      (recordings ?? []).map((recording) => ({
        ...recording,
        startDate: new Date(recording.startDate).toISOString(),
        endDate: new Date(recording.endDate).toISOString(),
      })),
    [],
  );

  const loadExpandedRewinds = useCallback(async () => {
    if (hasLoadedExpandedRewinds) return;
    if (expandedRewindsPromise.current) return expandedRewindsPromise.current;

    const requestKey = expandedRewindsRequestKey;
    const requestId = expandedRewindsRequestId.current + 1;
    expandedRewindsRequestId.current = requestId;
    setExpandedRewindsLoading(true);
    setExpandedRewindsError(false);

    const expandedPromise = (async () => {
      try {
        const expandedData = await (
          fetchCamInsightsRecording as (
            input: CamInsightsRecordingFetcherInput,
          ) => Promise<RewindRecording[] | null>
        )({
          camId: selectedCamera._id,
          startDate: fullRewindsRange.startDate,
          endDate: fullRewindsRange.endDate,
          surfHeightUnit,
        });
        if (
          expandedRewindsRequestKeyRef.current !== requestKey ||
          expandedRewindsRequestId.current !== requestId
        ) {
          return;
        }

        setData({
          cameraId: selectedCamera._id,
          requestKey,
          recordings: normalizeRecordingData(expandedData),
        });
        setHasLoadedExpandedRewinds(true);
      } catch {
        if (
          expandedRewindsRequestKeyRef.current !== requestKey ||
          expandedRewindsRequestId.current !== requestId
        ) {
          return;
        }

        setExpandedRewindsError(true);
      } finally {
        if (
          expandedRewindsRequestKeyRef.current === requestKey &&
          expandedRewindsRequestId.current === requestId
        ) {
          setExpandedRewindsLoading(false);
          expandedRewindsPromise.current = null;
        }
      }
    })();

    expandedRewindsPromise.current = expandedPromise;

    return expandedPromise;
  }, [
    expandedRewindsRequestKey,
    fullRewindsRange.endDate,
    fullRewindsRange.startDate,
    hasLoadedExpandedRewinds,
    normalizeRecordingData,
    selectedCamera._id,
    surfHeightUnit,
  ]);

  useEffect(() => {
    if (!hasInitializedRewindsRequest.current) {
      hasInitializedRewindsRequest.current = true;
      return;
    }

    setData(undefined);
    setRewindsPlaylists(undefined);
    setRewindsPlaylistsCameraId(undefined);
    setRewindsLoading(true);
    setExpandedRewindsLoading(false);
    setExpandedRewindsError(false);
    setHasLoadedExpandedRewinds(initialFetchIsFullHistory);
    setCurrentPlaylist(undefined);
    setCurrentPlaylistKey(undefined);
    setCurrentPlaylistCameraId(undefined);
    setNonPremiumPlaylistKey(undefined);
    setUserSelectedPlaylistKey(undefined);
    expandedRewindsPromise.current = null;
    expandedRewindsRequestId.current += 1;
    skipNextPlaylistSelection.current = false;
  }, [
    fullRewindsRange.endDate,
    fullRewindsRange.startDate,
    initialFetchIsFullHistory,
    initialRewindsRange.endDate,
    initialRewindsRange.startDate,
    selectedCamera._id,
    surfHeightUnit,
  ]);

  useEffect(() => {
    if (!routerIsReady || isLoading) {
      setRewindsLoading(true);
    }
  }, [isLoading, routerIsReady]);

  useEffect(() => {
    if (error) {
      setRewindsLoading(false);
    }
  }, [error]);

  useEffect(() => {
    if (data !== undefined && !error) {
      setData({
        cameraId: data.cameraId,
        requestKey: data.requestKey,
        recordings: normalizeRecordingData(data.recordings),
      });
    }

    if (error) {
      setData({
        cameraId: selectedCamera._id,
        requestKey: rewindsKey ? getRewindsRequestKey(rewindsKey) : selectedCamera._id,
        recordings: [],
      });
    }
  }, [data, error, normalizeRecordingData, rewindsKey, selectedCamera._id]);

  const selectCurrentPlaylist = useCallback(
    (endTimeOfPlaylist: string) => {
      const nextPlaylist = rewindsPlaylists?.get(endTimeOfPlaylist);
      setCurrentPlaylist(nextPlaylist);
      setCurrentPlaylistKey(endTimeOfPlaylist);
      setCurrentPlaylistCameraId(selectedCamera._id);
      setRewindsLoading(false);
    },
    [rewindsPlaylists, selectedCamera._id],
  );

  const updateCurrentPlaylist = useCallback(
    (endTimeOfPlaylist: string) => {
      skipNextPlaylistSelection.current = true;
      setUserSelectedPlaylistKey(endTimeOfPlaylist);
      const nextPlaylist = rewindsPlaylists?.get(endTimeOfPlaylist);
      if (nextPlaylist?.bucketStart) {
        setCurrentTimestamp(nextPlaylist.bucketStart);
      }
      selectCurrentPlaylist(endTimeOfPlaylist);
    },
    [rewindsPlaylists, selectCurrentPlaylist, setCurrentTimestamp],
  );

  const currentCameraPlaylist =
    currentPlaylistCameraId === selectedCamera._id ? currentPlaylist : undefined;

  const isCurrentPlaylistLatest = useMemo(() => {
    if (!currentCameraPlaylist) return false;

    const currentTime = new Date().getTime();
    const playlistEndTime = new Date(currentCameraPlaylist.bucketEnd).getTime();

    return currentTime - playlistEndTime <= PLAYLIST_BLOCK_LENGTH * oneMinute;
  }, [currentCameraPlaylist]);

  useEffect(() => {
    if (responseData) {
      const sortedRewindsPlaylists = generateSmartRewindPlaylistBlocks(
        responseData.recordings,
        REWIND_PLAYLIST_BLOCK_LENGTH_IN_MS,
        REWIND_PLAYLIST_BLOCK_OVERLAP_IN_MS,
      );
      setRewindsPlaylists(sortedRewindsPlaylists);
      setRewindsPlaylistsCameraId(responseData.cameraId);
    }
  }, [responseData]);

  useEffect(() => {
    if (
      responseData?.cameraId === selectedCamera._id &&
      responseData.recordings.length === 0 &&
      !error &&
      !expandedRewindsError &&
      !hasLoadedExpandedRewinds &&
      !expandedRewindsLoading &&
      !isLoading
    ) {
      void loadExpandedRewinds();
    }
  }, [
    error,
    expandedRewindsError,
    expandedRewindsLoading,
    hasLoadedExpandedRewinds,
    isLoading,
    loadExpandedRewinds,
    responseData,
    selectedCamera._id,
  ]);

  const currentCameraRewindsPlaylists =
    rewindsPlaylistsCameraId === selectedCamera._id ? rewindsPlaylists : undefined;

  const previouslyCompletedPlaylists = useMemo(
    () => ({
      morning: firstPreviouslyCompletePlaylist(
        currentCameraRewindsPlaylists,
        floorToMidnightForTZ(now, timezone),
      ),
      night: firstPreviouslyCompletePlaylist(currentCameraRewindsPlaylists, now),
    }),
    [currentCameraRewindsPlaylists, now, timezone],
  );

  const hasUsableInsightsPlaylist = useMemo(
    () => Array.from(rewindsPlaylists?.values() ?? []).some((entry) => entry.hasInsights),
    [rewindsPlaylists],
  );

  useEffect(() => {
    if (
      rewindsPlaylists?.size &&
      rewindsPlaylistsCameraId === selectedCamera._id &&
      !hasUsableInsightsPlaylist &&
      !error &&
      !expandedRewindsError &&
      !hasLoadedExpandedRewinds &&
      !expandedRewindsLoading &&
      !isLoading
    ) {
      void loadExpandedRewinds();
    }
  }, [
    error,
    expandedRewindsError,
    expandedRewindsLoading,
    hasLoadedExpandedRewinds,
    hasUsableInsightsPlaylist,
    isLoading,
    loadExpandedRewinds,
    rewindsPlaylists,
    rewindsPlaylistsCameraId,
    selectedCamera._id,
  ]);

  const getPlaylistForTimestamp = useCallback(
    (timestamp: number | undefined, playlists: RewindPlaylists) => {
      if (playlists?.size) {
        const firstKey = playlists.keys().next().value;
        if (firstKey) {
          const endTimeOfPlaylists = new Date(firstKey).getTime();
          if (timestamp && timestamp < endTimeOfPlaylists) {
            const matchingPlaylists = calculateMatchingPlaylistBlock(
              new Date(firstKey).getTime(),
              timestamp,
              REWIND_PLAYLIST_BLOCK_LENGTH_IN_MS,
              REWIND_PLAYLIST_BLOCK_OVERLAP_IN_MS,
            );
            return matchingPlaylists[matchingPlaylists.length - 1];
          }
        }
      }
      return undefined;
    },
    [],
  );

  const getNonPremiumPlaylistKey = useCallback(() => {
    if (paywallRedesignActive && nonPremiumRewindTime.time && rewindsPlaylists) {
      const matchingPlaylistKey = getPlaylistForTimestamp(
        nonPremiumRewindTime.time,
        rewindsPlaylists,
      );
      const matchedPlaylist = matchingPlaylistKey && rewindsPlaylists.get(matchingPlaylistKey);
      if (matchingPlaylistKey && matchedPlaylist && matchedPlaylist.rewinds.length) {
        return matchingPlaylistKey;
      }
    }
    return undefined;
  }, [paywallRedesignActive, nonPremiumRewindTime, getPlaylistForTimestamp, rewindsPlaylists]);

  useEffect(() => {
    if (
      rewindsPlaylists &&
      rewindsPlaylistsCameraId === selectedCamera._id &&
      !isLoading &&
      !(paywallRedesignActive && nonPremiumRewindTime.loading)
    ) {
      // If playlists is empty, stop loading
      if (rewindsPlaylists.size === 0) {
        setRewindsLoading(false);
        return;
      }

      if (skipNextPlaylistSelection.current) {
        skipNextPlaylistSelection.current = false;
        setRewindsLoading(false);
        return;
      }

      const currentNonPremiumPlaylistKey = getNonPremiumPlaylistKey();
      setNonPremiumPlaylistKey(currentNonPremiumPlaylistKey);

      // Explicit selector/scrubber choices win over playhead-based matching. Playback
      // timestamps lag behind clicks and previously snapped users to the prior window.
      if (userSelectedPlaylistKey && rewindsPlaylists.has(userSelectedPlaylistKey)) {
        selectCurrentPlaylist(userSelectedPlaylistKey);
        return;
      }

      if (
        currentTimestampRef.current.value &&
        (initialisedWithTimestamp.current || !currentNonPremiumPlaylistKey)
      ) {
        const matchingPlaylistKey = getPlaylistForTimestamp(
          currentTimestampRef.current.value.getTime(),
          rewindsPlaylists,
        );
        if (matchingPlaylistKey && rewindsPlaylists.get(matchingPlaylistKey)) {
          selectCurrentPlaylist(matchingPlaylistKey);
          return;
        }
      }

      if (nonPremiumRewindTime.time && currentNonPremiumPlaylistKey) {
        selectCurrentPlaylist(currentNonPremiumPlaylistKey);
        return;
      }

      const firstKey = rewindsPlaylists.keys().next().value;
      if (firstKey) {
        selectCurrentPlaylist(firstKey);
      }
    }
  }, [
    paywallRedesignActive,
    rewindsPlaylists,
    rewindsPlaylistsCameraId,
    selectedCamera._id,
    isLoading,
    getPlaylistForTimestamp,
    selectCurrentPlaylist,
    setCurrentTimestamp,
    currentTimestampRef,
    nonPremiumRewindTime,
    getNonPremiumPlaylistKey,
    userSelectedPlaylistKey,
  ]);

  const reFetch = useCallback(() => {
    setRewindsLoading(true);
    mutate();
  }, [mutate]);

  const rewindList = useMemo(
    () =>
      currentCameraPlaylist?.rewinds.map((rewind) => ({
        url: rewind.recordingUrl,
        startDateTime: rewind.startDate ? new Date(rewind.startDate) : new Date(),
        endDateTime: rewind.endDate ? new Date(rewind.endDate) : new Date(),
      })) || [],
    [currentCameraPlaylist],
  );

  const nextAvailableRewindFromLive = useMemo(() => {
    const latestRewindsArray = currentCameraRewindsPlaylists
      ? Array.from(currentCameraRewindsPlaylists.values()).find(
          (playlist) => playlist.rewinds.length > 0,
        )?.rewinds
      : undefined;
    return latestRewindsArray?.[latestRewindsArray.length - 1]?.startDate;
  }, [currentCameraRewindsPlaylists]);

  const contextValue = useMemo(
    () => ({
      rewindsPlaylists: currentCameraRewindsPlaylists,
      currentPlaylist: currentCameraPlaylist,
      updateCurrentPlaylist,
      error,
      reFetch,
      rewindsLoading,
      expandedRewindsLoading,
      expandedRewindsError,
      hasLoadedExpandedRewinds,
      loadExpandedRewinds,
      isCurrentPlaylistLatest,
      previouslyCompletedPlaylists,
      currentTimestamp,
      isNonPremiumPlaylist:
        !!currentCameraPlaylist &&
        !!nonPremiumPlaylistKey &&
        currentPlaylistKey === nonPremiumPlaylistKey,
      rewindList,
      nextAvailableRewindFromLive,
      bucketStartTimeInMs: currentCameraPlaylist?.bucketStart?.getTime() ?? null,
      bucketEndTimeInMs: currentCameraPlaylist?.bucketEnd?.getTime() ?? null,
    }),
    [
      rewindsLoading,
      expandedRewindsLoading,
      expandedRewindsError,
      hasLoadedExpandedRewinds,
      loadExpandedRewinds,
      currentCameraPlaylist,
      error,
      isCurrentPlaylistLatest,
      reFetch,
      previouslyCompletedPlaylists,
      currentCameraRewindsPlaylists,
      updateCurrentPlaylist,
      currentTimestamp,
      nonPremiumPlaylistKey,
      currentPlaylistKey,
      nextAvailableRewindFromLive,
      rewindList,
    ],
  );

  return <RewindsContext.Provider value={contextValue}>{children}</RewindsContext.Provider>;
};
